/////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////
// src/components/ProductCard.jsx — v25
// CHANGED FROM v24 (1 Aug 2026): REAL FIX — PageSpeed Insights repeatedly
// identified a Shop-grid product image as the actual LCP element, and it
// had a blanket loading="lazy" on it — telling the browser NOT to
// prioritize the single most important image on the page. Real sourced
// evidence (multiple independent case studies) shows removing lazy-load
// + adding fetchpriority="high" on the true LCP image is one of the
// highest-confidence fixes available for this exact problem. Added a new
// `priority` prop (default false): when true, the card's image loads
// eager + fetchpriority="high"; when false (the default, below-fold
// cards), it stays lazy exactly as before — this is NOT a blanket
// removal of lazy-loading sitewide, only the above-fold cards that are
// actually LCP candidates. Parent grids (Home.jsx, Shop.jsx,
// GenderHome.jsx) now pass priority={true} to their first few cards.
// CHANGED FROM v23 (31 Jul 2026): REAL FIX — removed the auto-rotate
// timer that cycled through every product's color images every 3
// seconds on the Shop grid, whether or not the card was actually
// visible/being looked at. Real evidence: DevTools Network tab showed
// ~200 image requests firing on a single Shop page load, and PageSpeed
// LCP stayed stubbornly at 13.5-15.5s across 4 separate runs even
// after image resizing, font, and cache fixes — none of which touched
// this. Every card's independent setInterval(3000ms) timers started
// within milliseconds of each other on page load, so they visually
// synced up (all cards appearing to change at once) and forced the
// browser to fetch every color variant for every product on the page,
// regardless of whether a customer had scrolled to it or was looking
// at it at all. Manual arrow navigation (‹ ›), already present on every
// card, is unaffected — customers can still browse colors, they're
// just no longer force-fetched automatically. autoRotatePaused state
// and its now-dead handler removed as part of this cleanup.
// CHANGED FROM v22 (31 Jul 2026): REAL FIX — reordered the ID row so the
// copy button sits directly after Prod ID, with the date pushed after
// the copy button (was ID → date → copy, now ID → copy → date), per
// Amit's correction.
// CHANGED FROM v21 (31 Jul 2026): added the post date next to Prod ID,
// separated by a "|" divider, on the same line (Amit's placement call).
// Uses product.updateDate (already a real Date object from
// useProducts.js), formatted as "28 Jul '26" via the new formatDate()
// helper. Only shown when updateDate exists — older/demo rows without
// a real date simply don't render the date span, no crash.
// CHANGED FROM v20 (30 Jul 2026): REAL FIX — the quick-add flow on
// the Shop grid (tap color, tap size, added straight from the card)
// never fired the Meta Pixel's AddToCart event — only the separate
// Product Detail page did. Confirmed via Events Manager: only 3
// AddToCart events in 5 days of live ad traffic against 1,100+
// ViewContent events, while ProductDetail.jsx's own AddToCart call
// was already correct. Since quick-add is very likely how most real
// customers actually add to cart (no extra page load), this single
// gap meant Meta's ad optimization had almost no real conversion
// signal to learn from — a likely real contributor to the campaign's
// poor performance so far, not just a reporting gap. doQuickAdd() now
// calls pixelAddToCart() on every successful add, same as
// ProductDetail.jsx already does.
/////////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { pixelAddToCart } from '../utils/pixel';
const GARMENT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const SHOE_SIZES = ['36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDate(date) {
  if (!date || isNaN(date.getTime())) return '';
  const day = date.getDate();
  const month = MONTH_ABBR[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  return `${day} ${month} '${year}`;
}
export default function ProductCard({ product, showResellerPrice = false, priority = false }) {
  const { toggleWishlist, wishlist, cart, addToCart } = useStore();
  const navigate = useNavigate();
  const [imgIndex, setImgIndex] = useState(0);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);
  const [confirmedColorIndex, setConfirmedColorIndex] = useState(null);
  const [justAdded, setJustAdded] = useState(false);
  const isWishlisted = wishlist.includes(product.id);
  const cartQtyForProduct = cart.filter(i => i.id === product.id).reduce((sum, i) => sum + i.qty, 0);
  const images = product.images?.length > 0 ? product.images : (product.imageUrl ? [product.imageUrl] : []);
  const hasMultipleImages = images.length > 1;
  const needsColorChoice = hasMultipleImages;
  const isPreviewConfirmed = confirmedColorIndex === imgIndex;
  const isShoe = product.category === 'Shoes';
  const standardSizes = isShoe ? SHOE_SIZES : GARMENT_SIZES;
  const availableSizes = product.sizes || [];
  const needsSize = !!product.hasSizing && (product.category === 'Garments' || product.category === 'Shoes') && !!product.sizes && product.sizes.length > 0;
  const colorConfirmedOrNotNeeded = !needsColorChoice || isPreviewConfirmed;
  const showRibbon = (needsColorChoice || needsSize) && !justAdded;
  const ribbonStage = !colorConfirmedOrNotNeeded ? 'color' : (needsSize ? 'size' : 'color');
  function colorCodeFor(i) { return `CC${String(i + 1).padStart(2, '0')}`; }
  function handleConfirmColor(e) {
    e.stopPropagation();
    e.preventDefault();
    setConfirmedColorIndex(imgIndex);
    if (!needsSize) {
      doQuickAdd(imgIndex, null);
    }
  }
  function handlePickSize(e, size) {
    e.stopPropagation();
    e.preventDefault();
    doQuickAdd(needsColorChoice ? imgIndex : null, size);
  }
  function doQuickAdd(colorImgIndex, size) {
    const colorCode = colorImgIndex !== null ? colorCodeFor(colorImgIndex) : null;
    const colorImageUrl = colorImgIndex !== null ? images[colorImgIndex] : '';
    const result = addToCart(product, 1, { size, colorCode, colorImageUrl });
    if (result?.ok) {
      pixelAddToCart(product, 1);
      setJustAdded(true);
      setTimeout(() => { setJustAdded(false); setConfirmedColorIndex(null); }, 1400);
    }
  }
  const displayPrice = showResellerPrice ? product.resellerPrice : product.price;
  const isGarment = product.category?.toLowerCase() === 'garments';
  const isSold = product.sold;
  const isFreeShip = product.shipping?.toUpperCase().includes('FREE');
  const categoryDisplay = product.subCategory?.trim()
    ? `${product.category} / ${product.subCategory}`
    : product.category;
  const formattedDate = formatDate(product.updateDate);
  function handleCopyId(e) {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard.writeText(product.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 1500);
  }
  function handleWishlist(e) {
    e.stopPropagation();
    e.preventDefault();
    toggleWishlist(product.id);
  }
  async function handleShare(e) {
    e.stopPropagation();
    e.preventDefault();
    const shareText = `${product.name}
${product.description?.substring(0, 200) || ''}
Product ID: ${product.id}`;
    try {
      const imageUrls = images.slice(0, 5);
      const imageFiles = [];
      for (const url of imageUrls) {
        try {
          const proxiedUrl = `/.netlify/functions/image-proxy?url=${encodeURIComponent(url)}`;
          const response = await fetch(proxiedUrl);
          if (!response.ok) throw new Error(`Image proxy returned ${response.status}`);
          const blob = await response.blob();
          const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
          const file = new File([blob], `product-${product.id}.${ext}`, { type: blob.type || 'image/jpeg' });
          imageFiles.push(file);
        } catch (imgErr) {
          console.error('Share: could not attach image', url, imgErr);
        }
      }
      if (navigator.share) {
        if (imageFiles.length > 0 && navigator.canShare && navigator.canShare({ files: imageFiles })) {
          await navigator.share({ title: product.name, text: shareText, files: imageFiles });
        } else {
          await navigator.share({ title: product.name, text: shareText });
        }
      } else {
        await navigator.clipboard.writeText(shareText);
        setCopiedShare(true);
        setTimeout(() => setCopiedShare(false), 1500);
      }
    } catch (err) {
      // User cancelled the native share sheet — no action needed
    }
  }
  function handlePrevImg(e) {
    e.stopPropagation();
    e.preventDefault();
    setImgIndex(i => (i - 1 + images.length) % images.length);
  }
  function handleNextImg(e) {
    e.stopPropagation();
    e.preventDefault();
    setImgIndex(i => (i + 1) % images.length);
  }
  function handleCardClick() {
    if (!isSold) navigate(`/product/${product.id}`);
  }
  return (
    <div
      className={`prod-card ${isSold ? 'prod-card-sold' : ''}`}
      onClick={handleCardClick}
      style={{ cursor: isSold ? 'default' : 'pointer' }}
    >
      <div
        className={`prod-img ${isGarment ? 'prod-img-g' : 'prod-img-j'}`}
      >
        {images.length > 0 ? (
          <img
            src={images[imgIndex]}
            alt={product.name}
            loading={priority ? 'eager' : 'lazy'}
            fetchpriority={priority ? 'high' : 'auto'}
          />
        ) : (
          <i className={`ti ${isGarment ? 'ti-shirt' : 'ti-diamond'}`} aria-hidden="true"></i>
        )}
        {isSold && (
          <div className="prod-sold-overlay">
            <span className="prod-sold-text">Sold Out</span>
          </div>
        )}
        {cartQtyForProduct > 0 && !isSold && (
          <div className="prod-cart-banner">
            <i className="ti ti-shopping-bag-check"></i> IN CART · {cartQtyForProduct}
          </div>
        )}
        {hasMultipleImages && !isSold && (
          <>
            <div className="prod-img-count">{imgIndex + 1}/{images.length}</div>
            <button className="prod-img-arrow prod-img-arrow-left" onClick={handlePrevImg} aria-label="Previous image">
              &#8249;
            </button>
            <button className="prod-img-arrow prod-img-arrow-right" onClick={handleNextImg} aria-label="Next image">
              &#8250;
            </button>
          </>
        )}
        {justAdded && (
          <div className="prod-added-overlay">
            <div className="prod-added-badge">
              <i className="ti ti-check"></i>
            </div>
            <div className="prod-added-text">Added to cart</div>
          </div>
        )}
        {!isSold && (
          <button
            className={`prod-img-wish ${isWishlisted ? 'wishlisted' : ''}`}
            onClick={handleWishlist}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <i className={`ti ${isWishlisted ? 'ti-heart-filled' : 'ti-heart'}`} aria-hidden="true"></i>
          </button>
        )}
        {showRibbon && ribbonStage === 'color' && (
          <button className="prod-quickadd-ribbon color" onClick={handleConfirmColor}>
            Select this color · {colorCodeFor(imgIndex)}
          </button>
        )}
        {showRibbon && ribbonStage === 'size' && (
          <div className="prod-quickadd-ribbon size" onClick={e => e.stopPropagation()}>
            {availableSizes.map(s => (
              <button key={s} className="prod-size-chip" onClick={e => handlePickSize(e, s)}>{s}</button>
            ))}
          </div>
        )}
      </div>
      <div className="prod-info">
        <div className="prod-name">{product.name}</div>
        <div className="prod-id-row" onClick={e => e.stopPropagation()}>
          <span className="prod-id">{product.id}</span>
          <button onClick={handleCopyId} className="prod-copy-btn" aria-label="Copy Product ID">
            <i className={`ti ${copiedId ? 'ti-check' : 'ti-copy'}`}></i>
          </button>
          {formattedDate && (
            <span className="prod-date">&nbsp;|&nbsp;{formattedDate}</span>
          )}
        </div>
        <div className="prod-cat">{categoryDisplay}</div>
        <div className="prod-footer">
          <div>
            <span className="prod-price">₹{displayPrice.toLocaleString()}</span>
            <span style={{
              fontSize: 9,
              marginLeft: 4,
              color: isFreeShip ? '#fff' : '#fff',
              fontWeight: 500,
              background: isFreeShip ? 'var(--green)' : '#E24B4A',
              padding: '2px 6px',
              borderRadius: 3,
            }}>
              {isFreeShip ? 'Free Ship' : 'Ship Extra'}
            </span>
          </div>
          {!isSold && (
            <button
              className="prod-add"
              onClick={handleShare}
              aria-label="Share product"
            >
              <i className={`ti ${copiedShare ? 'ti-check' : 'ti-share'}`} aria-hidden="true"></i>
            </button>
          )}
        </div>
      </div>
      <style>{`
        .prod-img-wish { position: absolute; top: 8px; right: 8px; z-index: 4; width: 30px; height: 30px; border-radius: 50%; background: #fff; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 4px rgba(0,0,0,0.25); -webkit-tap-highlight-color: transparent; transition: background 0.15s ease; }
        .prod-img-wish i { font-size: 17px; color: #B8B4AC; }
        .prod-img-wish.wishlisted { background: #FF3B5C !important; }
        .prod-img-wish.wishlisted i { color: #fff !important; }
        .prod-add { width: 44px; height: 44px; border-radius: 50%; background: #F4F2EE; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; position: relative; z-index: 5; -webkit-tap-highlight-color: transparent; }
        .prod-add i { font-size: 18px; color: var(--navy); pointer-events: none; }
        .prod-id-row { display: flex; align-items: center; gap: 4px; }
        .prod-date { font-size: 10px; color: var(--muted); white-space: nowrap; }
        .prod-cat { font-size: 11px; color: var(--muted); }
        .prod-quickadd-ribbon { position: absolute; bottom: 0; left: 0; right: 0; z-index: 4; text-align: center; padding: 8px 6px; font-size: 11px; font-weight: 600; border: none; cursor: pointer; font-family: var(--font); animation: ribbonFadeIn 0.2s ease; }
        .prod-quickadd-ribbon.color { background: #FF7A45; color: #fff; }
        .prod-quickadd-ribbon.size { background: #00B8A9; display: flex; gap: 4px; justify-content: center; padding: 6px 4px; overflow-x: auto; }
        .prod-added-overlay { position: absolute; inset: 0; z-index: 5; background: rgba(27,42,74,0.55); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; animation: overlayFadeIn 0.18s ease; }
        .prod-added-badge { width: 44px; height: 44px; border-radius: 50%; background: var(--green); display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.25); animation: addedPop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .prod-added-badge i { font-size: 22px; color: #fff; }
        .prod-added-text { color: #fff; font-size: 12px; font-weight: 600; letter-spacing: 0.02em; }
        @keyframes overlayFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes addedPop { 0% { transform: scale(0.5); opacity: 0; } 60% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
        .prod-size-chip { background: #fff; color: var(--blue); border: none; border-radius: 4px; padding: 4px 8px; font-size: 10px; font-weight: 600; cursor: pointer; font-family: var(--font); flex-shrink: 0; }
        @keyframes ribbonFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .prod-copy-btn { background: none; border: none; padding: 0; cursor: pointer; color: var(--muted); display: flex; align-items: center; }
        .prod-copy-btn i { font-size: 11px; }
        .prod-img { position: relative; }
        .prod-img-count { position: absolute; top: 8px; left: 8px; z-index: 3; background: rgba(0,0,0,0.55); color: #fff; font-size: 9px; font-weight: 500; padding: 2px 6px; border-radius: 8px; }
        .prod-cart-banner { position: absolute; top: 0; left: 0; right: 0; z-index: 4; background: var(--green); color: #fff; font-size: 12px; font-weight: 700; letter-spacing: 0.03em; padding: 7px 8px; text-align: center; display: flex; align-items: center; justify-content: center; gap: 6px; box-shadow: 0 2px 6px rgba(0,0,0,0.15); }
        .prod-cart-banner i { font-size: 14px; }
        .prod-img-arrow { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(255,255,255,0.8); border: none; color: var(--navy); font-size: 16px; width: 22px; height: 22px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 3; padding: 0; line-height: 1; }
        .prod-img-arrow-left { left: 6px; }
        .prod-img-arrow-right { right: 6px; }
      `}</style>
    </div>
  );
}